import { useEffect, useMemo, useState } from 'react'
import { canWriteArea, canWriteBelegung, type OrderListItemDTO } from '@wf/shared'
import { useAuth } from '../app/auth'
import { STATUS_LABEL } from '../orders/labels'
import { localYmd } from '../orders/workshop'
import { useOrdersList } from '../orders/useOrders'
import { useSettings } from '../settings/useSettings'
import { AssignDialog } from './AssignDialog'
import { PlanGrid } from './PlanGrid'
import { addDays, buildPlanDay, dayLabel, minutesOfDay, unscheduledBacklog } from './planung'

/**
 * Plantafel (W10): Arbeitsplätze × Zeitachse für EINEN Tag (MVP; Woche/DnD = Stufe 2). Daten kommen aus
 * der bestehenden Auftragsliste + den Werkstatt-Settings (bays/Öffnungszeiten) — kein eigener Endpunkt.
 * Zuweisung per Dialog (OCC-Patch). Auslastung über @wf/shared bayUtilization = derselbe Rechenweg wie
 * die Chef-Statistik. Redesign 15.07. (Simons Claude-Design-Entwurf, token-treu): KPI-Karten mit
 * Auslastungs-Balken, Status-Legende, „Jetzt"-Linie mit Minuten-Ticker.
 */
export function PlanungBoard() {
  const { user } = useAuth()
  const orders = useOrdersList()
  const settings = useSettings()
  // BEWUSST kein useMemo([]): ein Werkstatt-Tablet bleibt über Nacht auf /planung stehen — ein eingefrorenes
  // „heute" ließe den Heute-Chip morgens auf den VORTAG springen (Review-Fund 15.07.). Der Klick rechnet
  // zusätzlich selbst frisch, damit auch ohne Zwischen-Render der echte aktuelle Tag gesetzt wird.
  const today = localYmd(new Date())
  const [day, setDay] = useState(today)
  const [selected, setSelected] = useState<OrderListItemDTO | null>(null)
  // „Jetzt"-Linie: Minuten-Ticker, damit die Linie im Tagesverlauf wandert (60-s-Intervall reicht).
  const [nowMin, setNowMin] = useState(() => minutesOfDay(new Date()))
  useEffect(() => {
    const t = setInterval(() => setNowMin(minutesOfDay(new Date())), 60_000)
    return () => clearInterval(t)
  }, [])

  const bays = settings.data?.bays ?? 4
  const openStartMin = settings.data?.openStartMin ?? 480
  const openEndMin = settings.data?.openEndMin ?? 1020
  const canPlan = user != null && (canWriteArea(user, 'annahme') || canWriteBelegung(user))

  const all = useMemo(() => orders.data?.orders ?? [], [orders.data])
  const plan = useMemo(() => buildPlanDay(all, day, bays, openStartMin, openEndMin), [all, day, bays, openStartMin, openEndMin])
  const backlog = useMemo(() => unscheduledBacklog(all), [all])
  // Der Dialog braucht die FRISCHE Zeile (version!) — nach einem Patch invalidiert der Hook die Liste,
  // darum hier stets die aktuelle Listen-Zeile statt des beim Klick eingefrorenen Objekts anzeigen.
  const selectedFresh = selected ? (all.find((o) => o.id === selected.id) ?? null) : null

  if (orders.isLoading || settings.isLoading) return <div className="muted">Plantafel wird geladen…</div>
  if (orders.error || settings.error) {
    return (
      <div className="error" role="alert">
        Plantafel konnte nicht geladen werden.
      </div>
    )
  }

  const auslastung = plan.utilizationPercent
  return (
    <section className="wf-view">
      <div className="wf-plan__head">
        <div>
          <h2 className="viewtitle">Plantafel</h2>
          <p className="sub">{dayLabel(day)}</p>
        </div>
        {/* KPI-Karten (Design-Entwurf): Zahlen mit Kontext statt nackter Textzeile. */}
        <div className="wf-plan__kpis" role="status">
          <div className="wf-plan__kpi">
            <span className="wf-plan__kpilabel">Termine</span>
            <span className="wf-plan__kpivalue">{plan.slotCount}</span>
          </div>
          <div className="wf-plan__kpi">
            <span className="wf-plan__kpilabel">Auslastung</span>
            <span className="wf-plan__kpivalue wf-plan__kpivalue--accent">{auslastung != null ? `${auslastung} %` : '–'}</span>
            <span className="wf-plan__meter" aria-hidden="true">
              <span className="wf-plan__meterfill" style={{ width: `${Math.min(100, Math.max(0, auslastung ?? 0))}%` }} />
            </span>
          </div>
          <div className="wf-plan__kpi">
            <span className="wf-plan__kpilabel">Nicht eingeplant</span>
            <span className={`wf-plan__kpivalue${plan.unplaced.length + backlog.length > 0 ? ' wf-plan__kpivalue--warn' : ''}`}>
              {plan.unplaced.length + backlog.length}
            </span>
          </div>
        </div>
      </div>

      <div className="wf-filterbar">
        <div className="wf-chips" role="group" aria-label="Tag wählen">
          <button type="button" className="wf-chip" onClick={() => setDay(addDays(day, -1))} aria-label="Vortag">
            ‹
          </button>
          <button
            type="button"
            className={day === today ? 'wf-chip wf-chip--active' : 'wf-chip'}
            onClick={() => setDay(localYmd(new Date()))}
          >
            Heute
          </button>
          <button type="button" className="wf-chip" onClick={() => setDay(addDays(day, 1))} aria-label="Folgetag">
            ›
          </button>
        </div>
        <input
          className="wf-input wf-plan__datepick"
          type="date"
          value={day}
          aria-label="Datum wählen"
          onChange={(e) => e.target.value && setDay(e.target.value)}
        />
        {/* Status-Legende (Design-Entwurf) — dieselben Tokens wie die Karten-Kanten. */}
        <div className="wf-plan__legend" aria-hidden="true">
          {(['offen', 'arbeit', 'wartet', 'fertig'] as const).map((s) => (
            <span key={s} className="wf-plan__legenditem">
              <span className={`wf-plan__legenddot wf-plan__legenddot--${s}`} />
              {STATUS_LABEL[s]}
            </span>
          ))}
        </div>
      </div>

      <PlanGrid
        plan={plan}
        backlog={backlog}
        openStartMin={openStartMin}
        openEndMin={openEndMin}
        nowMin={nowMin}
        showNow={day === today}
        onSelect={setSelected}
      />

      {selectedFresh ? (
        <AssignDialog order={selectedFresh} day={day} bays={bays} canPlan={canPlan} onClose={() => setSelected(null)} />
      ) : null}
    </section>
  )
}
